import { Nullable } from "@core/domain/types/nullable.type";
import {
    CreateDepositDto,
    DepositDto,
    SplitDepositDto,
} from "@entity/data/dto/deposit.dto";
import {
    DepositAnotherSourcesFinancingTypeEnum,
    DepositOwnFinancingTypeEnum,
    DepositSubventionTypeEnum,
} from "@entity/domain/models/deposit-type.model";
import {
    CreateDeposit,
    Deposit,
    SplitDeposit,
} from "@entity/domain/models/deposit.model";
import { IdentificationTypeEnum } from "@entity/domain/models/identification-type.model";
import { validateSync } from "class-validator";
import { inject, injectable } from "inversify";
import { IdentificationEnumMapper } from "./identification-enum.mapper";
import { PaymentMethodEnumMapper } from "./payment-method-enum.mapper";
import { IvaExemptEnumMapper } from "./iva-exempt-enum.mapper";
import { PaymentMethodTypeEnum } from "@entity/domain/models/payment-method.model";
import { IvaExemptTypeEnum } from "@entity/domain/models/iva-exempt-type.model";
import { IrpfRetentionTypeEnum } from "@entity/domain/models/irpf-retention-type.model";
import { IrpfRetentionEnumMapper } from "./irpf-retention-enum.mapper";
import { IrpfRetentionPercentageEnumMapper } from "./irpf-retention-percentage-enum.mapper";
import { IrpfRetentionPercentageTypeEnum } from "@entity/domain/models/irpf-retention-percentage-type.model";
import { ReasonEnumMapper } from "./reason-enum.mapper";
import { ReasonTypeEnum } from "@entity/domain/models/reason-type.model";

@injectable()
export class DepositMapper {
    constructor(
        @inject(IdentificationEnumMapper)
        private readonly identificationEnumMapper: IdentificationEnumMapper,
        private readonly paymentMethodEnumMapper: PaymentMethodEnumMapper,
        private readonly ivaExemptionTypeEnumMapper: IvaExemptEnumMapper,
        private readonly irpfRetentionTypeEnumMapper: IrpfRetentionEnumMapper,
        private readonly irpfRetentionPercentageTypeEnumMapper: IrpfRetentionPercentageEnumMapper,
        private readonly reasonTypeEnumMapper: ReasonEnumMapper,
    ) { }
    map(depositDto: DepositDto): Nullable<Deposit> {
        const errors = validateSync(depositDto);

        if (errors.length) {
            console.error(errors);
            return null;
        }

        let paymentMethod: PaymentMethodTypeEnum | null = null;
        if (depositDto.payment_method) {
            paymentMethod = this.paymentMethodEnumMapper.map(
                depositDto.payment_method,
            ) ?? null;
        }

        let ivaExemptionType: IvaExemptTypeEnum | null = null;
        if (depositDto.iva_exemption_type) {
            ivaExemptionType = this.ivaExemptionTypeEnumMapper.map(
                depositDto.iva_exemption_type,
            ) ?? null;
        }

        let irpfRetentionType: IrpfRetentionTypeEnum | null = null;
        if (depositDto.irpf_retention_type) {
            irpfRetentionType = this.irpfRetentionTypeEnumMapper.map(
                depositDto.irpf_retention_type,
            ) ?? null;
        }

        let irpfRetentionPercentageType: IrpfRetentionPercentageTypeEnum | null = null;
        if (depositDto.irpf_retention_percentage_type) {
            irpfRetentionPercentageType = this.irpfRetentionPercentageTypeEnumMapper.map(
                depositDto.irpf_retention_percentage_type,
            ) ?? null;
        }

        let reasonType: ReasonTypeEnum | null = null;
        if (depositDto.reason_type) {
            reasonType = this.reasonTypeEnumMapper.map(
                depositDto.reason_type,
            ) ?? null;
        }

        let identificationType = null;
        if (depositDto.identification_type) {
            identificationType = this.identificationEnumMapper.map(
                depositDto.identification_type,
            );
        }

        let recipientIdentificationType = null;
        if (depositDto.recipient_identification_type) {
            recipientIdentificationType = this.identificationEnumMapper.map(
                depositDto.recipient_identification_type,
            );
        }

        // Map hierarchical fields from both snake_case and camelCase
        const type = depositDto.type ?? depositDto.deposit_type_id ?? null;
        const subtype = depositDto.subtype ?? depositDto.deposit_subtype_id ?? null;
        const category = depositDto.category ?? depositDto.deposit_category_id ?? null;
        const scope = depositDto.scope ?? depositDto.deposit_scope_id ?? null;
        const scopeDetail = depositDto.scope_detail ?? "";

        return new Deposit(
            depositDto.id,
            depositDto.concept,
            // Removed ownFinancingType, subventionType, othersSourceFinancingType
            depositDto.income_linked_another_project ?? null,
            depositDto.amount,
            depositDto.name ?? "",
            depositDto.deposit_date,
            paymentMethod,
            ivaExemptionType,
            irpfRetentionType,
            irpfRetentionPercentageType,
            reasonType,
            identificationType ?? IdentificationTypeEnum.nif,
            depositDto.identification,
            depositDto.street,
            recipientIdentificationType ?? IdentificationTypeEnum.nif,
            depositDto.recipient_identification,
            depositDto.recipient_street,
            depositDto.financing_organization,
            depositDto.issuer,
            depositDto.recipient,
            depositDto.other_payment_source,
            depositDto.expedient_number,
            depositDto.deposit_year ?? "",
            depositDto.tax,
            depositDto.base_amount,
            depositDto.entity,
            undefined,
            depositDto.document ?? undefined,
            depositDto.communication_date ?? "",
            depositDto.invoice_issue_date ?? "",
            depositDto.resolution_date ?? "",
            depositDto.execution_date ?? "",
            depositDto.others_type ?? undefined,
            depositDto.observations ?? undefined,
            depositDto.document_data?.name,
            type,
            subtype,
            category,
            scope,
            scopeDetail,
        );
    }

    splitDepositMap(splitDepositDto: SplitDepositDto): Nullable<SplitDeposit> {
        const errors = validateSync(splitDepositDto);

        if (errors.length) {
            console.error(errors);
            return null;
        }

        return new SplitDeposit(
            splitDepositDto.id,
            splitDepositDto.deposit,
            splitDepositDto.project,
            splitDepositDto.assigned_percentage,
        );
    }

    mapToDepositDto(deposit: Deposit): DepositDto {
        const depositDto = new DepositDto();

        depositDto.payment_method = deposit.paymentMethod
            ? this.paymentMethodEnumMapper.mapToDto(deposit.paymentMethod)
            : null;

        depositDto.iva_exemption_type = deposit.ivaExemptionType
            ? this.ivaExemptionTypeEnumMapper.mapToDto(deposit.ivaExemptionType)
            : null;

        depositDto.irpf_retention_type = deposit.irpfRetentionType
            ? this.irpfRetentionTypeEnumMapper.mapToDto(deposit.irpfRetentionType)
            : null;

        depositDto.irpf_retention_percentage_type = deposit.irpfRetentionPercentageType
            ? this.irpfRetentionPercentageTypeEnumMapper.mapToDto(deposit.irpfRetentionPercentageType)
            : null;

        depositDto.reason_type = deposit.reasonType
            ? this.reasonTypeEnumMapper.mapToDto(deposit.reasonType)
            : null;

        depositDto.identification_type = deposit.identificationType
            ? this.identificationEnumMapper.mapToDto(deposit.identificationType)
            : null;

        depositDto.recipient_identification_type = deposit.recipientIdentificationType
            ? this.identificationEnumMapper.mapToDto(deposit.recipientIdentificationType)
            : null;

        depositDto.id = deposit.id;
        depositDto.concept = deposit.concept;
        depositDto.amount = deposit.amount;
        depositDto.deposit_date = deposit.depositDate;
        depositDto.entity = deposit.entity;
        depositDto.street = deposit.street;
        depositDto.recipient_street = deposit.recipientStreet;
        if (deposit.depositYear) depositDto.deposit_year = deposit.depositYear;
        depositDto.tax = deposit.tax;
        depositDto.base_amount = deposit.baseAmount;
        if (deposit.communicationDate) depositDto.communication_date = deposit.communicationDate;
        if (deposit.invoiceIssueDate) depositDto.invoice_issue_date = deposit.invoiceIssueDate;
        if (deposit.resolutionDate) depositDto.resolution_date = deposit.resolutionDate;
        if (deposit.executionDate) depositDto.execution_date = deposit.executionDate;
        if (deposit.othersType) depositDto.others_type = deposit.othersType;
        if (deposit.observations) depositDto.observations = deposit.observations;
        depositDto.name = deposit.name;
        depositDto.identification = deposit.identification;
        depositDto.recipient_identification = deposit.recipientIdentification;
        depositDto.financing_organization = deposit.financingOrganization;
        depositDto.issuer = deposit.issuer;
        depositDto.recipient = deposit.recipient;
        depositDto.other_payment_source = deposit.otherPaymentSource;
        depositDto.expedient_number = deposit.expedientNumber;
        if (deposit.relatedDocument) depositDto.document = deposit.relatedDocument;
        // Map hierarchical fields to backend names
        depositDto.type = deposit.depositTypeId ?? deposit.type ?? null;
        depositDto.subtype = deposit.depositSubtypeId ?? deposit.subtype ?? null;
        depositDto.category = deposit.depositCategoryId ?? deposit.category ?? null;
        depositDto.scope = deposit.depositScopeId ?? deposit.scope ?? null;
        depositDto.scope_detail = deposit.scopeDetail ?? null;

        return depositDto;
    }

    mapToCreateDepositDto(deposit: CreateDeposit): CreateDepositDto {
        const depositDto = new CreateDepositDto();

        depositDto.payment_method = deposit.paymentMethod
            ? this.paymentMethodEnumMapper.mapToDto(deposit.paymentMethod)
            : null;

        depositDto.iva_exemption_type = deposit.ivaExemptionType
            ? this.ivaExemptionTypeEnumMapper.mapToDto(deposit.ivaExemptionType)
            : null;

        depositDto.irpf_retention_type = deposit.irpfRetentionType
            ? this.irpfRetentionTypeEnumMapper.mapToDto(deposit.irpfRetentionType)
            : null;

        depositDto.irpf_retention_percentage_type = deposit.irpfRetentionPercentageType
            ? this.irpfRetentionPercentageTypeEnumMapper.mapToDto(deposit.irpfRetentionPercentageType)
            : null;

        depositDto.reason_type = deposit.reasonType
            ? this.reasonTypeEnumMapper.mapToDto(deposit.reasonType)
            : null;

        depositDto.identification_type = deposit.identificationType
            ? this.identificationEnumMapper.mapToDto(deposit.identificationType)
            : null;

        depositDto.recipient_identification_type = deposit.recipientIdentificationType
            ? this.identificationEnumMapper.mapToDto(deposit.recipientIdentificationType)
            : null;

        depositDto.concept = deposit.concept;
        depositDto.amount = deposit.amount;
        depositDto.deposit_date = deposit.depositDate;
        depositDto.entity = deposit.entity;
        depositDto.street = deposit.street;
        depositDto.recipient_street = deposit.recipientStreet;
        if (deposit.depositYear) depositDto.deposit_year = deposit.depositYear;
        depositDto.tax = deposit.tax;
        depositDto.base_amount = deposit.baseAmount;
        if (deposit.communicationDate) depositDto.communication_date = deposit.communicationDate;
        if (deposit.invoiceIssueDate) depositDto.invoice_issue_date = deposit.invoiceIssueDate;
        if (deposit.resolutionDate) depositDto.resolution_date = deposit.resolutionDate;
        if (deposit.executionDate) depositDto.execution_date = deposit.executionDate;
        if (deposit.othersType) depositDto.others_type = deposit.othersType;
        if (deposit.observations) depositDto.observations = deposit.observations;
        depositDto.name = deposit.name;
        depositDto.identification = deposit.identification;
        depositDto.recipient_identification = deposit.recipientIdentification;
        depositDto.financing_organization = deposit.financingOrganization;
        depositDto.issuer = deposit.issuer;
        depositDto.recipient = deposit.recipient;
        depositDto.other_payment_source = deposit.otherPaymentSource;
        depositDto.expedient_number = deposit.expedientNumber;
        if (deposit.relatedDocument) depositDto.document = deposit.relatedDocument;
        // Map hierarchical fields to backend names
        depositDto.type = deposit.depositTypeId ?? deposit.type ?? null;
        depositDto.subtype = deposit.depositSubtypeId ?? deposit.subtype ?? null;
        depositDto.category = deposit.depositCategoryId ?? deposit.category ?? null;
        depositDto.scope = deposit.depositScopeId ?? deposit.scope ?? null;
        depositDto.scope_detail = deposit.scopeDetail ?? null;

        return depositDto;
    }
}
